import {SagaIterator} from 'redux-saga';
import {call, debounce, put, select, takeEvery} from 'redux-saga/effects';

import {FieldType} from 'desktop/pages/checkout/types/fieldType';
import {ActionType} from 'types/reducers';
import {ShippingMethodType} from 'types/shippingMethodsType';

import {
    getShippingMethod,
    setShippingMethod,
} from '../../reducers/deliveryStep/shippingMethods/shippingMethods';
import {
    setAddressFieldValue,
    setShippingMethodsOnAutocomplete,
} from '../../reducers/sagaStep/sagaStep';
import {getShippingAddress} from '../../selectors/shippingAddress/shippingAddress';
import {getShippingMethodsSaga} from '../deliveryStep/getShippingMethodsSaga/getShippingMethodsSaga';

/**
 * Prepare shipping methods
 * @param {Array<ShippingMethodType>} list - current list
 */
export function* prepareShippingMethods({
    payload: {data},
}: ActionType<{data: Array<ShippingMethodType>}>): SagaIterator {
    let selectedShippingMethod = data.find(method => method.selected);

    if (!selectedShippingMethod) {
        const [cheapest] = data.sort(
            (currentMethod, previousMethod) =>
                Number(currentMethod.price) - Number(previousMethod.price),
        );
        selectedShippingMethod = cheapest;
    }

    yield put(
        setShippingMethod({
            error: '',
            list: data,
            selected: selectedShippingMethod ? selectedShippingMethod.code : '',
            status: 'FULFILLED',
        }),
    );
}

/**
 * Get new shipping methods if user meets requirement for two-day shipping
 * @param {ActionType<UpdateDataType>} action - Result of update
 */
export function* checkForTwoDayDelivery({
    payload: {fieldName, target},
}: ActionType<FieldType>): SagaIterator {
    // Optimization to trigger only on shippingAddress change
    // Should maintain only postal field
    if (target !== 'shippingAddress') return;
    const shippingAddress = yield select(getShippingAddress);

    // Only for US citizen
    if (fieldName !== 'postal' || shippingAddress?.country?.value !== 'US') return;

    yield call(getShippingMethodsSaga);
}

/**
 * Checkout update shipping method saga
 */
export function* updateShippingMethodSaga(): SagaIterator {
    yield debounce(800, `${setAddressFieldValue}`, checkForTwoDayDelivery);
    yield debounce(800, `${setShippingMethodsOnAutocomplete}`, getShippingMethodsSaga);

    yield takeEvery(`${getShippingMethod.fulfilled}`, prepareShippingMethods);
}
